import * as React from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { useFetchQueryParams } from '@/hooks/useFetchQueryParams';
import { useNavigate } from '@/router';

import { useGetTrackingInfo } from './useGetTrackingInfo';

export const useToggleCommentsPanel = ({
  onClick,
  replace,
}: UseToggleCommentsPanelType = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { url } = useRouteMatch();
  const { tab_name, feed_name } = useGetTrackingInfo();

  const { comments, type } = useFetchQueryParams();

  const toggleSearchParams = React.useCallback(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (type === 'preview') {
      if (comments) {
        trackEvent('comment_section_closed', {
          tab_name,
          feed_name,
        });
        urlSearchParams.delete('overlay-comments');
      } else {
        urlSearchParams.set('overlay-comments', 'true');
      }
    } else {
      if (urlSearchParams.has('comments')) {
        trackEvent('comment_section_closed', {
          tab_name,
          feed_name,
        });
        urlSearchParams.delete('comments');
      } else {
        urlSearchParams.set('comments', 'true');
      }
    }

    return urlSearchParams.toString();
  }, [comments, feed_name, tab_name, type]);

  return React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      if (e.defaultPrevented) {
        return;
      }

      const method = replace ? 'replace' : 'push';

      navigate[method](
        {
          pathname: url,
          search: toggleSearchParams(),
        },
        {
          location,
          ...location.state,
        },
        {
          removeOldQueryParams: true,
          removeOldState: true,
        }
      );
    },
    [navigate, location, onClick, replace, toggleSearchParams, url]
  );
};

type UseToggleCommentsPanelType = {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  replace?: boolean;
};
